import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { Fragment, useEffect } from "react";

import { Auth } from "components/types";
import { useAuthDispatchContext } from "core/auth/hooks";
import { userSettingsStorageKey } from "core/user.settings/context";
import { API_ENV_STORAGE_KEY, getApiBaseUrl } from "./environment";

interface Props {
    children?: React.ReactNode;
}

// axios instance
const instance = axios.create({
    baseURL: getApiBaseUrl(),
    headers: {'Accept': 'application/json'},
});

const NetworkInterceptor = ({ children }: Props) => {
    const dispatch = useAuthDispatchContext();

    useEffect(() => {
        const onRequest = (config: AxiosRequestConfig): AxiosRequestConfig => {
            const auth = localStorage.getItem('auth') !== null && JSON.parse(localStorage.getItem('auth') || "{}");
            const userSettings = localStorage.getItem(userSettingsStorageKey) !== null && JSON.parse(localStorage.getItem(userSettingsStorageKey) || "{}");
        
            if (auth && config.headers) {
                config.headers["access-token"] = auth.accessToken;
                config.headers["token-type"] = auth.tokenType;
                config.headers["expiry"] = auth.expiry;
                config.headers["uid"] = auth.uid;
                config.headers["client"] = auth.client;
            }
            if (userSettings && config.headers) {
                config.headers["X-Agency-Id"] = userSettings['agency_id'];
            }

            return config;
        };

        const onRequestError = (error: AxiosError): Promise<AxiosError> => {
            return Promise.reject(error);
        };

        const onResponse = (response: AxiosResponse): AxiosResponse => {
            if (response.headers['access-token']) {
                const auth : Auth = {
                    accessToken: response.headers['access-token'],
                    tokenType: response.headers['token-type'],
                    expiry: response.headers['expiry'],
                    uid: response.headers['uid'],
                    client: response.headers['client'],
                }
                localStorage.setItem('auth', JSON.stringify(auth));
                dispatch(auth);
            }
        
            return response;
        };

        const onResponseError = async (error: AxiosError): Promise<AxiosError> => {
            if (error.response) {
              if (error.response.status === 401) {
                // Keep the dev-only API environment choice across forced logouts
                const apiEnv = localStorage.getItem(API_ENV_STORAGE_KEY);
                localStorage.clear();
                if (apiEnv) localStorage.setItem(API_ENV_STORAGE_KEY, apiEnv);
                dispatch(null);
              }
            }
            return Promise.reject(error);
        };

        const requestInterceptor = instance.interceptors.request.use(onRequest, onRequestError);
        const responseInterceptor = instance.interceptors.response.use(onResponse, onResponseError);

        return () => {
            instance.interceptors.request.eject(requestInterceptor);
            instance.interceptors.response.eject(responseInterceptor);
        };
    }, [dispatch]);

    return <Fragment>{children}</Fragment>;
};

export default instance;
export { NetworkInterceptor };
